import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TextNode, $getRoot, ElementNode } from 'lexical';
import React, { useEffect } from 'react';
import { lexicalTextEditorConfig } from '../editors';
import { ColorlessTextNode } from '../nodes/ColorlessTextNode';
import { $isReferenceCitationNode, ReferenceCitationNode } from '../nodes/ReferenceCitationNode';
import { ReferenceCitationSeparatorNode } from '../nodes/ReferenceCitationSeparatorNode';
import { $setNodeTextWithNumbersRange } from '../Plugins/ReferenceCitationPlugin/utils/setNodeText';
import { ResetStylesPlugin } from '../Plugins/ResetStylesPlugin';
import { mainTheme } from '../Theme/mainTheme';

const editorConfig = {
  namespace: 'text-preview-editor',
  editable: false,
  theme: mainTheme,
  onError: (error: Error): void => {
    throw error;
  },
  nodes: [
    ...lexicalTextEditorConfig.nodes,
    ColorlessTextNode,
    ReferenceCitationNode,
    ReferenceCitationSeparatorNode,
    {
      replace: TextNode,
      with: (node: TextNode): ColorlessTextNode => new ColorlessTextNode(node.__text),
      withKlass: ColorlessTextNode,
    },
  ],
};

function ApplyPreviewNumbersPlugin({
  referencesMap,
}: {
  referencesMap: Record<string, number>;
}): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const stack = [...$getRoot().getChildren()];

      while (stack.length) {
        const node = stack.pop();
        if ($isReferenceCitationNode(node)) {

          $setNodeTextWithNumbersRange(node, referencesMap, true);
        }

        if (node instanceof ElementNode) {
          stack.push(...node .getChildren());
        }
      }
    });
  }, [editor, referencesMap]);

  return null;
}

type Props = {
  editorState: string | null;
  referencesMap: Record<string, number>;
};

export const LexicalTextPreviewEditor = (props: Props): JSX.Element => {
  const { editorState, referencesMap } = props;

  return (
    <LexicalComposer initialConfig={{
      ...editorConfig,
      editorState,
    }} >
      <div className="editor-container">
        <RichTextPlugin
          contentEditable={<ContentEditable className="editor-input" data-testid="editor-input"/>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ApplyPreviewNumbersPlugin referencesMap={referencesMap} />
        <ResetStylesPlugin/>
      </div>
    </LexicalComposer>
  );
};
